import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../common/Icons';
import './AdminPersonnelModal.css';

/**
 * Modal unifié admin personnel (overlay custom, thème ap-*).
 */
export default function AdminPersonnelModal({
  show,
  onHide,
  title,
  badge,
  subtitle,
  icon,
  size = 'md',
  footer,
  children,
  ariaLabelledBy = 'ap-modal-title'
}) {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onHide();
    },
    [onHide]
  );

  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  return createPortal(
    <div
      className="ap-modal-overlay"
      onClick={onHide}
      role="presentation"
    >
      <div
        className={`ap-modal ap-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ap-modal__header">
          <div className="ap-modal__header-main">
            {icon && (
              <span className="ap-modal__header-icon" aria-hidden>
                <Icon name={icon} size={18} />
              </span>
            )}
            <div>
              {badge && <span className="ap-modal__badge">{badge}</span>}
              <h2 id={ariaLabelledBy}>{title}</h2>
              {subtitle && <p className="ap-modal__subtitle">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            className="ap-modal__close"
            onClick={onHide}
            aria-label="Fermer"
          >
            <Icon name="x" size={16} />
          </button>
        </header>

        <div className="ap-modal__body">{children}</div>

        {footer != null && <footer className="ap-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}

export function ModalDetailField({ label, children, fullWidth = false }) {
  return (
    <div className={`ap-modal-detail__field ${fullWidth ? 'ap-modal-detail__field--full' : ''}`}>
      <span className="ap-modal-detail__label">{label}</span>
      <div className="ap-modal-detail__value">{children}</div>
    </div>
  );
}

export function ModalFormSection({ title, hint, children }) {
  return (
    <section className="ap-modal-section">
      {title && (
        <div className="ap-modal-section__head">
          <h3 className="ap-modal-section__title">{title}</h3>
          {hint && <p className="ap-modal-section__hint">{hint}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

export function ModalFormGroup({
  label,
  required,
  htmlFor,
  children,
  className = '',
  fullWidth = false
}) {
  return (
    <div
      className={`ap-modal-field ${fullWidth ? 'ap-modal-field--full' : ''} ${className}`.trim()}
    >
      {label && (
        <label className="ap-modal-field__label" htmlFor={htmlFor}>
          {label}
          {required && <span className="ap-modal-field__req" aria-hidden> *</span>}
        </label>
      )}
      {children}
    </div>
  );
}

export function ModalFooterActions({ onCancel, cancelLabel = 'Annuler', primary }) {
  return (
    <>
      <button type="button" className="ap-modal-btn ap-modal-btn--ghost" onClick={onCancel}>
        {cancelLabel}
      </button>
      {primary}
    </>
  );
}
